import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearchLocation } from 'elements/editor/interaction/SearchLocation/useSearchLocation';
import { SelectValueStyle } from 'elements/minute/sidebar/sidebarComponents/atomic/SelectValue/SelectValueStyle';
import { EColors, EFonts } from 'libs/styles/styling';
import { useEffect, useRef, useState } from 'react';
import { ICoordinates } from 'react-give-me-map';
import Select from 'react-select';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';
import { useDebounce } from 'usehooks-ts';

interface Value {
    label: string;
    value: string;
}

interface Props {
    palceholder?: string;
    onLocationSelected?: (params: {
        lat: number;
        lng: number;
        placeId: string;
        placeText?: string;
        bounds?: { leftTop: ICoordinates; rightBottom: ICoordinates };
    }) => void;
    className?: string;
}

export const LocationSearchInput = (props: Props): JSX.Element => {
    const [textValue, setTextValue] = useState('');
    const debouncedValue = useDebounce(textValue, 250);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ref = useRef<any>(null);
    const searchLocation = useSearchLocation();

    useEffect(() => {
        if (debouncedValue) {
            searchLocation.findLocations(debouncedValue);
        }
    }, [debouncedValue]);

    const items =
        textValue.length >= 3
            ? searchLocation.items?.map<Value>((e) => ({ label: e.text, value: e.id })) ?? []
            : [];

    return (
        <S_LocationSearchInput className={props.className}>
            <S_Icon
                icon={['fal', 'magnifying-glass']}
                onClick={() => {
                    ref.current?.focus();
                }}
            />
            <S_Container>
                <Select<Value>
                    ref={ref}
                    value={null}
                    onInputChange={(value, action) => {
                        if (action.action === 'menu-close') {
                            setTextValue(action.prevInputValue);
                            return;
                        }
                        setTextValue(value);
                    }}
                    onChange={async (newValue: Value) => {
                        console.log('newValue', newValue);
                        const res = await searchLocation.geolocate(newValue.value, newValue.label);
                        if (res) {
                            props.onLocationSelected?.(res);
                            searchLocation.setItems([]);
                            setTextValue('');
                        }
                    }}
                    openMenuOnFocus={false}
                    options={items}
                    isLoading={searchLocation.isLoadingItems}
                    noOptionsMessage={({ inputValue }) => {
                        if (searchLocation.isLoadingItems) {
                            return Localization.LOADING + '...';
                        }
                        if (inputValue.length < 3) {
                            return Localization.PLEASE_WRITE_CHARACTERS;
                        }
                        return Localization.NOTHING_FOUND;
                    }}
                    loadingMessage={() => {
                        return Localization.LOADING + '...';
                    }}
                    styles={{
                        ...SelectValueStyle,
                        input: (provided) => ({
                            ...provided,
                            paddingLeft: '40px',
                            fontFamily: EFonts.Regular
                        }),
                        placeholder: (provided) => ({
                            ...provided,
                            paddingLeft: '40px',
                            fontFamily: EFonts.Regular
                        }),
                        noOptionsMessage: (provided) => ({
                            ...provided,
                            fontSize: '13px',
                            color: EColors.DARK,
                            fontFamily: EFonts.Regular
                        }),
                        loadingMessage: (provided) => ({
                            ...provided,
                            fontSize: '13px',
                            color: EColors.DARK,
                            fontFamily: EFonts.Regular
                        }),
                        control: (provided) => ({
                            ...provided,
                            border: '1px solid #E7E7E7',
                            boxShadow: 'none !important',
                            borderRadius: '7px',
                            '&:hover': {
                                border: '1px solid #E7E7E7'
                            },
                            '&:focus': {
                                border: '1px solid #E7E7E7',
                                boxShadow: 'none',
                                outline: 'none'
                            }
                        })
                    }}
                    placeholder={props.palceholder ?? Localization.LOCATION}
                    components={{
                        ClearIndicator: () => null,
                        DropdownIndicator: () => null
                    }}
                />
            </S_Container>
        </S_LocationSearchInput>
    );
};

const S_LocationSearchInput = styled.div`
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    z-index: 1000;
`;

const S_Icon = styled(FontAwesomeIcon)`
    color: #141c54;
    font-size: 20px;
    width: 20px;
    left: 15px;
    position: absolute;
    z-index: 1;
    vertical-align: middle;
    cursor: pointer;
`;

const S_Container = styled.div`
    width: 100%;
    font-size: 14px;
    font-family: ${EFonts.Regular};
    border-radius: 7px;
`;
